import { useState, useEffect, useRef, useCallback } from 'react';
import { getBookingList } from '../services/api';

// Store audio context globally to avoid creating multiple instances
let globalAudioContext = null;
let audioContextInitialized = false;

/**
 * Initialize audio context on user interaction
 * This must be called after a user gesture (click, touch, etc.)
 */
const initializeAudioContext = () => {
  if (audioContextInitialized && globalAudioContext) {
    return globalAudioContext;
  }

  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) {
      return null;
    }

    globalAudioContext = new AudioContextClass();
    audioContextInitialized = true;
    return globalAudioContext;
  } catch (error) {
    return null;
  }
};

/**
 * Get or create audio context (will be null if not initialized)
 */
const getAudioContext = () => {
  return globalAudioContext;
};

/**
 * Play a notification sound using Web Audio API
 * Creates a pleasant, short notification sound
 */
const playNotificationSound = () => {
  try {
    const audioContext = getAudioContext();
    if (!audioContext) {
      return;
    }

    // Resume audio context if suspended (required by some browsers)
    const playSound = () => {
      try {
        // Create a pleasant notification sound (two-tone chime)
        const oscillator1 = audioContext.createOscillator();
        const oscillator2 = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // First tone: 800Hz
        oscillator1.type = 'sine';
        oscillator1.frequency.setValueAtTime(800, audioContext.currentTime);
        
        // Second tone: 1000Hz (plays slightly after first)
        oscillator2.type = 'sine';
        oscillator2.frequency.setValueAtTime(1000, audioContext.currentTime);
        
        // Envelope for smooth fade in/out
        const now = audioContext.currentTime;
        gainNode.gain.setValueAtTime(0, now);
        gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
        gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
        
        // Connect oscillators to gain node
        oscillator1.connect(gainNode);
        oscillator2.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Start oscillators
        oscillator1.start(now);
        oscillator2.start(now + 0.05); // Slight delay for harmony
        
        // Stop after 300ms
        oscillator1.stop(now + 0.3);
        oscillator2.stop(now + 0.3);
      } catch (error) {
        // Silently fail
      }
    };

    if (audioContext.state === 'suspended') {
      audioContext.resume().then(() => {
        playSound();
      }).catch(() => {
        // Silently fail
      });
    } else if (audioContext.state === 'running') {
      playSound();
    }
  } catch (error) {
    // Silently fail
  }
};

const STORAGE_KEY_PREFIX = 'roomnexa_booking_notifications_seen_';
const MAX_SEEN_IDS = 100;

function getSeenIdsFromStorage(hotelId) {
  try {
    const key = `${STORAGE_KEY_PREFIX}${hotelId ?? 'all'}`;
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.filter((id) => id != null && String(id).trim() !== '') : [];
  } catch {
    return [];
  }
}

function saveSeenIdToStorage(hotelId, bookingId) {
  if (bookingId == null || bookingId === '') return;
  try {
    const key = `${STORAGE_KEY_PREFIX}${hotelId ?? 'all'}`;
    const ids = getSeenIdsFromStorage(hotelId);
    const id = String(bookingId);
    if (ids.includes(id)) return;
    ids.push(id);
    if (ids.length > MAX_SEEN_IDS) ids.splice(0, ids.length - MAX_SEEN_IDS);
    localStorage.setItem(key, JSON.stringify(ids));
  } catch {
    // ignore
  }
}

/**
 * Custom hook for managing booking notifications
 * Polls for new bookings and shows notifications
 * 
 * @param {Object} options
 * @param {number} options.hotelId - Hotel ID to filter bookings
 * @param {number} options.pollInterval - Polling interval in milliseconds (default: 10000)
 * @param {number} options.maxNotifications - Maximum number of notifications to show (default: 5)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @param {boolean} options.playSound - Whether to play notification sound (default: true)
 */
export const useBookingNotifications = ({
  hotelId,
  pollInterval = 10000, // 10 seconds
  maxNotifications = 5,
  enabled = true,
  playSound = true,
} = {}) => {
  const [notifications, setNotifications] = useState([]);
  const [lastCheckedTime, setLastCheckedTime] = useState(new Date());
  const lastBookingIdsRef = useRef(new Set(getSeenIdsFromStorage(hotelId)));
  const pollIntervalRef = useRef(null);

  // Keep ref in sync with localStorage when hotelId changes
  useEffect(() => {
    lastBookingIdsRef.current = new Set(getSeenIdsFromStorage(hotelId));
  }, [hotelId]);

  // Fetch new bookings
  const checkForNewBookings = useCallback(async () => {
    if (!hotelId || !enabled) {
      return;
    }

    try {
      const now = new Date();
      // Get bookings from last 2 minutes to catch recent webhook bookings
      const since = new Date(now.getTime() - 2 * 60 * 1000);

      const response = await getBookingList({
        created_at__gte: since.toISOString(),
        sort: '-created_at',
        per_page: 20,
      });

      // Handle different response structures (PaginatedListMixin returns 'Data' with capital D)
      const bookings = response?.Data || response?.results || response?.bookings || response?.data?.Data || response?.data?.results || response?.data?.bookings || [];
      
      // Normalize id so number vs string doesn't cause same booking to reappear
      const normalizeId = (id) => (id != null ? String(id) : null);

      // Filter out bookings we've already seen (or user has dismissed, including after refresh)
      const newBookings = bookings.filter((booking) => {
        const bookingId = normalizeId(booking.id);
        if (!bookingId) return false;
        const alreadySeen = lastBookingIdsRef.current.has(bookingId);
        if (alreadySeen) {
          return false;
        }
        lastBookingIdsRef.current.add(bookingId);
        saveSeenIdToStorage(hotelId, bookingId);
        return true;
      });

      // Only show OTA bookings (from webhooks)
      const otaBookings = newBookings.filter((booking) => {
        const isOta = booking.booking_source === 'ota';
        const hasChannel = !!(booking.channel || booking.channel_data);
        return isOta && hasChannel;
      });

      if (otaBookings.length > 0) {
        // Play notification sound
        if (playSound) {
          playNotificationSound();
        }
        
        // Add new notifications
        setNotifications((prev) => {
          const combined = [...otaBookings, ...prev];
          // Keep only the most recent notifications
          return combined.slice(0, maxNotifications);
        });
      }

      setLastCheckedTime(now);
    } catch (error) {
      // Silently handle errors
    }
  }, [hotelId, enabled, maxNotifications, playSound]);

  // Initialize audio context on user interaction
  useEffect(() => {
    const initAudio = () => {
      initializeAudioContext();
    };

    // Initialize on any user interaction
    const events = ['click', 'touchstart', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, initAudio, { once: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, initAudio);
      });
    };
  }, []);

  // Start polling
  useEffect(() => {
    if (!enabled || !hotelId) {
      return;
    }

    // Initial check
    checkForNewBookings();

    // Set up polling interval
    pollIntervalRef.current = setInterval(() => {
      checkForNewBookings();
    }, pollInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [enabled, hotelId, pollInterval, maxNotifications, checkForNewBookings]);

  // Remove notification (dismiss) — mark as seen so it never reappears (even after refresh)
  const removeNotification = useCallback((bookingId) => {
    const id = bookingId != null ? String(bookingId) : null;
    if (id) {
      lastBookingIdsRef.current.add(id);
      saveSeenIdToStorage(hotelId, id);
    }
    setNotifications((prev) => prev.filter((n) => (n.id != null ? String(n.id) : n.id) !== id));
  }, [hotelId]);

  // Clear all notifications
  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    notifications,
    removeNotification,
    clearAllNotifications,
    lastCheckedTime,
  };
};
